import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from './config.service';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class PredictAnalysisService {
  constructor(private http: HttpClient) {}

  public load(teamName: string): Observable<any> {
    return this.http.get(`${ConfigService.apiUrl}/predict-analysis/${teamName}`);
  }

  public getPredictions(teamName: string, model: string, date: string): Observable<any> {
    return this.http.get(`${ConfigService.apiUrl}/predict-analysis/${teamName}/${model}/${date}`);
  }

  public getPredictionInputs(teamName: string, engine: string, timestamp: string): Observable<any> {
    return this.http.get(`${ConfigService.apiUrl}/predict-inputs/${teamName}/${engine}/${timestamp}`);
  }

  public getAdhocDays(teamName: string, path: string): Observable<any> {
    return this.http.get(`${ConfigService.apiUrl}/adhoc-days/${teamName}/${encodeURIComponent(path)}`);
  }

  public getAdhocPredictions(teamName: string, model: string, date: string, path: string): Observable<any> {
    return this.http.get(`${ConfigService.apiUrl}/adhoc-predict/${teamName}/${model}/${date}/${encodeURIComponent(path)}`);
  }

  public getAdhocPredictionInputs(teamName: string, model: string, timestamp: string, path: string): Observable<any> {
    return this.http.get(`${ConfigService.apiUrl}/adhoc-predict-inputs/${teamName}/${model}/${timestamp}/${encodeURIComponent(path)}`);
  }

  public makePrediction(teamName: string, engine: string, model: string, timestamp: string, changes: object): Observable<any> {
    return this.http.post<any[]>(
      `${ConfigService.apiUrl}/predict-analysis/make-prediction/${teamName}/${engine}/${model}/${timestamp}`,
      changes
    );
  }

  public getModelInfo(teamName: string, modelName: string): Observable<any> {
    return this.http.get<any>(
      `${ConfigService.apiUrl}/predict-analysis/${teamName}/model-info/${modelName}`
    );
  }

  public invokeModel(teamName: string, modelName: string, inputs?: any): Observable<any> {
    const body = inputs ? { inputs } : {};
    return this.http.post<any>(
      `${ConfigService.apiUrl}/predict-analysis/${teamName}/invoke/${modelName}`,
      body
    );
  }

  public getImpactAnalysis(teamName: string, engine: string, model: string, timestamp: string): Observable<any> {
    return this.http.get<any[]>(
      `${ConfigService.apiUrl}/predict-analysis/impact/${teamName}/${engine}/${model}/${timestamp}`
    );
  }

  public getImpactFromInputs(teamName: string, modelName: string, inputs: any): Observable<any> {
    return this.http.post<any>(
      `${ConfigService.apiUrl}/predict-analysis/${teamName}/impact/${modelName}`,
      { inputs }
    );
  }

  public browse(teamName: string, modelName: string, options?: { offset?: number, limit?: number, sort?: string, filter?: string }): Observable<any> {
    let params = new HttpParams();
    if (options?.offset) params = params.set('offset', options.offset.toString());
    if (options?.limit) params = params.set('limit', options.limit.toString());
    if (options?.sort) params = params.set('sort', options.sort);
    if (options?.filter) params = params.set('filter', options.filter);
    return this.http.get<any>(
      `${ConfigService.apiUrl}/predict-analysis/${teamName}/browse/${modelName}`,
      { params }
    );
  }

  public inspectSample(teamName: string, modelName: string, sample: number): Observable<any> {
    return this.http.get<any>(
      `${ConfigService.apiUrl}/predict-analysis/${teamName}/inspect/${modelName}/${sample}`
    );
  }

  public prepareExport(teamName: string, modelName: string): Observable<{ path: string, samples: number, cached: boolean }> {
    return this.http.get<{ path: string, samples: number, cached: boolean }>(
      `${ConfigService.apiUrl}/predict-analysis/${teamName}/export/${modelName}`
    );
  }

  public parseImport(teamName: string, modelName: string, filePath: string): Observable<any> {
    return this.http.post<any>(
      `${ConfigService.apiUrl}/predict-analysis/${teamName}/parse-import/${modelName}`,
      { filePath }
    );
  }

  public predictBatch(teamName: string, modelName: string, filePath: string): Observable<any> {
    return this.http.post<any>(
      `${ConfigService.apiUrl}/predict-analysis/${teamName}/predict-batch/${modelName}`,
      { filePath }
    );
  }

}
